import React, { useState, useCallback } from 'react';
import Popup, { PopupType } from './Popup';

interface PopupData {
  type: PopupType;
  title: string;
  message: string;
  duration?: number;
}

interface PopupContainerProps {
  popup: PopupData | null;
  onClose: () => void;
}

const PopupContainer: React.FC<PopupContainerProps> = ({ popup, onClose }) => {
  if (!popup) {
    return null;
  }

  return (
    <Popup
      type={popup.type}
      title={popup.title}
      message={popup.message}
      duration={popup.duration}
      onClose={onClose}
    />
  );
};

export default PopupContainer;

// Hook for managing popup
export const usePopup = () => {
  const [popup, setPopup] = useState<PopupData | null>(null);

  const showPopup = useCallback((
    type: PopupType,
    title: string,
    message: string,
    duration: number = 2500
  ) => {
    setPopup({ type, title, message, duration });
  }, []);

  const closePopup = useCallback(() => {
    setPopup(null);
  }, []);

  const showSuccess = useCallback((title: string, message: string, duration?: number) => {
    showPopup('success', title, message, duration);
  }, [showPopup]);

  const showError = useCallback((title: string, message: string, duration?: number) => {
    showPopup('error', title, message, duration);
  }, [showPopup]);

  const showWarning = useCallback((title: string, message: string, duration?: number) => {
    showPopup('warning', title, message, duration);
  }, [showPopup]);

  const showInfo = useCallback((title: string, message: string, duration?: number) => {
    showPopup('info', title, message, duration);
  }, [showPopup]);

  return {
    popup,
    closePopup,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};
